import { createTokenRefresher } from './tokenRefresh.mjs'
import { readSession, writeSession } from './session.mjs'

const PLATFORM_BASE_URL = (
  process.env.VUE_APP_PLATFORM_BASE_URL || 'http://127.0.0.1:19176'
).replace(/\/$/, '')

export class PlatformRequestError extends Error {
  constructor(message, status, code) {
    super(message)
    this.name = 'PlatformRequestError'
    this.status = status
    this.code = code
  }
}

// 关键节点：会话过期处理器由 auth store 注册，刷新失败时清理登录态并打开登录弹窗。
let sessionExpiredHandler = null
export function setSessionExpiredHandler(handler) {
  sessionExpiredHandler = typeof handler === 'function' ? handler : null
}

function accessToken() {
  const session = readSession(window.localStorage)
  return session ? session.accessToken : null
}

// 关键节点：单飞刷新器，多个并发 401 共享同一个刷新请求；刷新失败清 session 并通知登录弹窗。
const tokenRefresher = createTokenRefresher({
  getSession: () => readSession(window.localStorage),
  saveSession: session => writeSession(window.localStorage, session),
  requestRefresh: refreshToken => {
    return request(
      '/auth/refresh',
      {
        ...jsonOptions('POST', { refresh_token: refreshToken }),
        skipAuth: true
      }
    )
  },
  onRefreshFailed: () => {
    writeSession(window.localStorage, null)
    if (sessionExpiredHandler) {
      sessionExpiredHandler()
    }
  }
})

async function doFetch(path, options, headers) {
  return fetch(`${PLATFORM_BASE_URL}${path}`, { ...options, headers })
}

async function handleResponse(response) {
  if (response.status === 204) {
    return null
  }
  let body = null
  try {
    body = await response.json()
  } catch (error) {
    body = null
  }
  if (!response.ok) {
    const platformError = body && body.error
    const detail = body && body.detail
    const message =
      (platformError && platformError.message) ||
      (detail && detail.message) ||
      (typeof detail === 'string' ? detail : '') ||
      '统一平台请求失败'
    throw new PlatformRequestError(
      message,
      response.status,
      platformError && platformError.code
    )
  }
  return body
}

async function request(path, options = {}) {
  const buildHeaders = () => {
    const headers = {
      'Content-Type': 'application/json',
      ...(options.headers || {})
    }
    const token = accessToken()
    if (token && !options.skipAuth) {
      headers.Authorization = `Bearer ${token}`
    }
    return headers
  }

  let response = await doFetch(path, options, buildHeaders())

  // 关键节点：401 时先用 refresh_token 换新 token，再重放原请求一次。
  if (response.status === 401 && !options.skipAuth) {
    try {
      await tokenRefresher.refreshSession()
    } catch (error) {
      // 刷新失败时 session 已被清理，抛出明确的过期错误供页面提示
      throw new PlatformRequestError(
        '登录已过期，请重新登录',
        401,
        'AUTH_SESSION_EXPIRED'
      )
    }
    response = await doFetch(path, options, buildHeaders())
  }

  return handleResponse(response)
}

function jsonOptions(method, payload, headers) {
  return {
    method,
    headers,
    body: payload === undefined ? undefined : JSON.stringify(payload)
  }
}

export const platform = {
  sendSms(phone) {
    return request(
      '/auth/sms/send',
      {...jsonOptions('POST', { phone }), skipAuth: true}
    )
  },
  verifySms(phone, code) {
    return request(
      '/auth/sms/verify',
      {...jsonOptions('POST', { phone, code }), skipAuth: true}
    )
  },
  me() {
    return request('/auth/me')
  },
  refresh(refreshToken) {
    return request(
      '/auth/refresh',
      {...jsonOptions('POST', { refresh_token: refreshToken }), skipAuth: true}
    )
  },
  logout(refreshToken) {
    return request(
      '/auth/logout',
      {...jsonOptions('POST', { refresh_token: refreshToken }), skipAuth: true}
    )
  },
  catalog() {
    return request('/catalog', { skipAuth: true })
  },
  startTrial(planSku, trackIds) {
    return request(
      '/trials',
      jsonOptions('POST', { plan_sku: planSku, track_ids: trackIds })
    )
  },
  entitlements() {
    return request('/me/entitlements')
  },
  // 方法：查询当前登录用户的钱包余额
  wallet() {
    return request('/me/wallet')
  },
  // 方法：查询公开充值档位，未登录时同样可用
  rechargePlans() {
    return request('/recharge/plans', { skipAuth: true })
  },
  issueSsoCode(clientId, redirectUri) {
    return request(
      '/sso/codes',
      jsonOptions('POST', {
        client_id: clientId,
        redirect_uri: redirectUri
      })
    )
  },
  createOrder(payload, idempotencyKey) {
    return request(
      '/orders',
      jsonOptions('POST', payload, {
        'Idempotency-Key': idempotencyKey
      })
    )
  },
  // 方法：创建充值订单，写操作携带 Idempotency-Key 防止重复下单
  createRechargeOrder(payload, idempotencyKey) {
    return request(
      '/recharge/orders',
      jsonOptions('POST', payload, {
        'Idempotency-Key': idempotencyKey
      })
    )
  },
  getOrder(orderId) {
    return request(`/orders/${orderId}`)
  },
  orders() {
    return request('/orders')
  },
  preparePayment(orderId, channel) {
    return request(
      `/orders/${orderId}/pay`,
      jsonOptions('POST', { channel })
    )
  },
  queryPayment(orderId) {
    return request(
      `/orders/${orderId}/payment-query`,
      jsonOptions('POST')
    )
  },
  confirmMockPayment(orderId, scenario = 'success') {
    return request(
      `/payments/mock/${orderId}/confirm`,
      jsonOptions('POST', { scenario })
    )
  },
  closeOrder(orderId) {
    return request(
      `/orders/${orderId}/close`,
      jsonOptions('POST')
    )
  },
  // 方法：查询当前用户发票列表
  invoices() {
    return request('/invoices')
  },
  // 方法：申请发票，写操作必须携带 Idempotency-Key
  createInvoice(payload, idempotencyKey) {
    return request(
      '/invoices',
      jsonOptions('POST', payload, {
        'Idempotency-Key': idempotencyKey
      })
    )
  }
}

export function createIdempotencyKey(prefix = 'checkout') {
  if (window.crypto && window.crypto.randomUUID) {
    return `${prefix}-${window.crypto.randomUUID()}`
  }
  return `${prefix}-${Date.now()}-${Math.random().toString(16).slice(2)}`
}
